import { useSyncExternalStore } from 'react';

export interface SharedState<T> {
  get(): T;
  set(next: T): void;
  subscribe(listener: () => void): () => void;
}

export function createSharedState<T>(initial: T): SharedState<T> {
  let value = initial;
  const listeners = new Set<() => void>();

  return {
    get: () => value,
    set: (next: T) => {
      value = next;
      listeners.forEach((listener) => listener());
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/**
 * Subscribes a component to a value created with createSharedState.
 * Multiple components using the same SharedState instance share the same value and are all notified on change.
 *
 * In the future this could be replaced by a library like Zustand if we want better DX or other features like selectors.
 */
export function useSharedState<T>(state: SharedState<T>) {
  const value = useSyncExternalStore(state.subscribe, state.get);
  return [value, state.set] as const;
}
